import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    Signal,
    signal,
    WritableSignal
} from "@angular/core";
import {CurrencyPipe} from '../../../utils/currency.pipe';
import {ResponsiveService} from "../../../responsive/responsive.service";
import {AnalyticsService} from '../../../logging/analytics.service';
import {navParam} from "../../../navigation/navigation-params";
import {Page, PageGeometry} from "../../../navigation/page";
import {MenuCategoryData, MenuExtraGroupData, MenuItemData} from '../../../menu/menu-entity.model';
import {MenuModel} from '../../../menu/menu.model';
import {DealModel} from '../../../deal/deal.model';
import {OrderModel} from "../../order.model";
import {OrderingContextModel} from '../../../ordering-context/ordering-context.model';
import {ContentMessageComponent} from "../../../notification/message/content-message.component";
import {FooterMessageComponent} from "../../../notification/message/footer-message.component";
import {MessageLayout, MessageLevel} from "../../../notification/message/message";
import {NotificationService} from "../../../notification/notification.service";
import {FedeltaImageData} from "@fedelta-pos/data-client";
import {OrderItem} from '../../order';
import {ButtonComponent} from "../../../buttons/button/button.component";
import {ButtonConnotation, ButtonProminence} from "../../../buttons/button/button";
import {LinkComponent} from "../../../buttons/link/link.component";
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {FullPageLayoutComponent} from '../../../page-layout/full-page-layout/full-page-layout.component';
import {ModalPageLayoutComponent} from '../../../page-layout/modal-page-layout/modal-page-layout.component';
import {DietaryPanelComponent} from "../dietary-panel/dietary-panel.component";
import {OrderItemOptionsPanelComponent} from "../order-item-options-panel/order-item-options-panel.component";
import {OrderItemQuantityControlComponent} from "../order-item-quantity-control/order-item-quantity-control.component";
import {StickyDirective} from '../../../ui-elements/sticky.directive';
import {ImageCacheDirective} from '../../../ui-elements/image/image-cache.directive';
import {HtmlPipe} from '../../../utils/html.pipe';
import {LiteralPipe} from '../../../utils/literal.pipe';
import {PageFooterControlDirective} from '../../../page-layout/page-footer-control.directive';
import {ConfigurationModel} from '../../../configuration/configuration.model';

class PromotionResult {
    constructor(public changesMade: boolean, public proceed: boolean) {
    }
}

@Component({
    selector: "woa-order-item-edit-page",
    templateUrl: "./order-item-edit-page.component.html",
    styleUrl: "./order-item-edit-page.component.scss",
    providers: [{provide: Page, useExisting: OrderItemEditPageComponent}],
    imports: [
        ButtonComponent,
        ContentMessageComponent,
        CurrencyPipe,
        DietaryPanelComponent,
        FooterMessageComponent,
        FullPageLayoutComponent,
        HtmlPipe,
        ImageCacheDirective,
        LinkComponent,
        LiteralPipe,
        ModalPageLayoutComponent,
        NgClass,
        OrderItemOptionsPanelComponent,
        OrderItemQuantityControlComponent,
        PageFooterControlDirective,
        StickyDirective,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderItemEditPageComponent extends Page {
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public menuModel: MenuModel = inject(MenuModel);
    public dealModel: DealModel = inject(DealModel);
    public responsiveService: ResponsiveService = inject(ResponsiveService);
    private notificationService: NotificationService = inject(NotificationService);
    private orderModel: OrderModel = inject(OrderModel);
    private analyticsService: AnalyticsService = inject(AnalyticsService);

    MessageLevel = MessageLevel;
    MessageLayout = MessageLayout;
    ButtonConnotation = ButtonConnotation;
    ButtonProminence = ButtonProminence;

    public readonly category: Signal<MenuCategoryData> = navParam<MenuCategoryData>("category");
    public readonly originalOrderItem: Signal<OrderItem | undefined> = navParam<OrderItem>("orderItem");
    public readonly itemsToOrder: Signal<OrderItem[] | undefined> = navParam<OrderItem[]>("itemsToOrder");
    private readonly menuItemParam: Signal<MenuItemData | undefined> = navParam<MenuItemData>("menuItem");

    public menuItem: WritableSignal<MenuItemData> = signal<MenuItemData>(
        this.originalOrderItem() ? this.originalOrderItem().menuItem() : (this.menuItemParam() ?? this.itemsToOrder()[0].menuItem()));

    public orderItem: WritableSignal<OrderItem> = signal<OrderItem>(
        this.originalOrderItem() ? this.originalOrderItem().createClone()
            : this.menuItemParam() ? this.orderModel.createOrderItem(this.menuItem(), this.menuItem().sellable.minWebOrderQty || 1)
                : this.itemsToOrder()[0]);

    private orderItemIndex: WritableSignal<number> = signal<number>(0);
    public mobileHeaderStuck: WritableSignal<boolean> = signal<boolean>(false);
    public contentVisible: WritableSignal<boolean> = signal<boolean>(true);
    public itemConfirmed: WritableSignal<boolean> = signal<boolean>(false);

    public readonly editing: Signal<boolean> = computed(() => !!this.originalOrderItem());
    public readonly singleItem: Signal<boolean> = computed(() => !!this.originalOrderItem() || !!this.menuItemParam());

    public readonly informational: Signal<boolean> = computed<boolean>(() =>
        this.category()?.informational || this.menuItem()?.informational);
    private readonly isSimpleProduct: Signal<boolean> = computed(() =>
        this.singleItem() ? this.menuModel.isSimpleProductItem()(this.menuItem()) : false);
    private readonly allAreSimpleProducts: Signal<boolean> = computed(() =>
        !this.singleItem() && this.itemsToOrder() ? this.itemsToOrder().every((orderItem: OrderItem) =>
            this.menuModel.isSimpleProductItem()(orderItem.menuItem(), true)) : false);
    private readonly containsDetails: Signal<boolean> = computed(() =>
        this.singleItem() ? this.menuModel.itemSummaryContainsDetails()(this.menuItem()) : false);
    private readonly anySummaryContainsDetails: Signal<boolean> = computed(() =>
        !this.singleItem() && this.itemsToOrder() ? this.itemsToOrder().some((orderItem: OrderItem) =>
            this.menuModel.itemSummaryContainsDetails()(orderItem.menuItem(), true)) : false);

    public readonly productImage: Signal<FedeltaImageData> = computed<FedeltaImageData>(() =>
        this.menuModel.itemImage()(undefined, this.menuItem(), false));

    public readonly titleParams: Signal<Record<string, string | number>> = computed<Record<string, string | number>>(() => ({
        "itemName": this.menuModel.getMenuItemDisplayName(this.menuItem()),
        "dealName": this.dealModel.getDealName(this.dealModel.pendingDeal()),
        "itemIndex": this.orderItemIndex() + 1,
        "nrItems": this.itemsToOrder() && this.itemsToOrder().length,
        "dealMessage": this.dealModel.pendingDeal() && this.dealModel.pendingDeal().description
    }));

    public readonly isLastOrderItem: Signal<boolean> = computed<boolean>(() => this.itemsToOrder() && this.orderItemIndex() === this.itemsToOrder().length - 1);

    public readonly addItemToOrderLabel: Signal<string> = computed<string>(() =>
        this.singleItem() ? (!this.editing() ? "woa.button.order.item-edit.addQtyForPrice" : this.orderItem().quantitySignal() ? "woa.button.order.item-edit.confirmAtPrice" : "woa.button.order.item-edit.confirmRemoval")
            : this.isLastOrderItem() ? (this.itemsToOrder().length > 1 ? "woa.button.order.item-edit.addDealItemsToOrder" : "woa.button.order.addToOrder")
                : "woa.button.order.item-edit.nextDealItem");

    public readonly desktopCustomGeometry: Signal<PageGeometry> = computed<PageGeometry>(() => ({
        pillarboxedOnDesktop: "none",
        width: this.showDesktopOptionsPanel() ? "90%" : "450px",
        height: this.showDesktopOptionsPanel() ? "95%" : this.productImage() && this.productImage().id && this.hasSummaryContent() ? "660px" : "507px"
    }));

    // Note: we display a wait time information notice if a standard wait time is set, and is not exceeded by the current wait time.
    public readonly showItemWaitTimeInfo: Signal<boolean> = computed<boolean>(() =>
        this.menuItem().sellable.standardWaitTimeMinutes && (this.menuItem().sellable.standardWaitTimeMinutes >= (this.menuItem().sellable.currentWaitTimeMinutes || 0)) && this.orderingContextModel.showWaitTimeNotices());

    // Note: we display a wait time warning notice if the current wait time exceeds the standard wait time.
    public readonly showItemWaitTimeWarning: Signal<boolean> = computed<boolean>(() =>
        (this.menuItem().sellable.currentWaitTimeMinutes || 0) > (this.menuItem().sellable.standardWaitTimeMinutes || 0) && this.orderingContextModel.showWaitTimeNotices());

    public desktopContentStickyTop: Signal<(content: HTMLElement) => string> = computed(() => {
        const pageScrollerER: ElementRef | undefined = this.pageLayout()?.pageScrollerER();
        return pageScrollerER ? (content: HTMLElement) => Math.min(pageScrollerER.nativeElement.clientHeight - content.offsetHeight, 0) + "px" : () => undefined;
    });

    public readonly title: Signal<string> = computed(() =>
        this.singleItem() ? "woa.heading.order.item-edit.itemTitle"
            : this.itemsToOrder() && this.itemsToOrder().length > 1 ? "woa.heading.order.item-edit.dealTitleWithProgress"
                : "woa.heading.order.item-edit.dealTitle");

    public readonly subtitle: Signal<string | undefined> = computed(() =>
        this.itemsToOrder() ? "woa.heading.order.item-edit.dealSubtitle" : undefined);

    public readonly hasSummaryContent: Signal<boolean> = computed(() =>
        this.singleItem() ? this.containsDetails() : this.anySummaryContainsDetails());

    public readonly showDesktopOptionsPanel: Signal<boolean> = computed(() =>
        this.singleItem() ? !this.isSimpleProduct() : !this.allAreSimpleProducts());

    private itemsOrdered: boolean = false;
    private extraGroupInteracted: MenuExtraGroupData[] = [];

    constructor() {
        super();
        this.orderModel.addToOrderAttempted.set(false);
    }

    override onDidDismissPage(): void {
        if (!this.singleItem() && !this.itemsOrdered) {
            this.dealModel.cancelPendingDeal();
        }
    }

    private hasExtraGroupInteracted(extraGroup: MenuExtraGroupData): boolean {
        return this.extraGroupInteracted.indexOf(extraGroup) >= 0;
    }

    public setExtraGroupInteracted(extraGroup: MenuExtraGroupData): void {
        if (!this.hasExtraGroupInteracted(extraGroup)) {
            this.extraGroupInteracted.push(extraGroup);
        }
    }

    public async editNotes(): Promise<void> {
        if (this.orderItem().itemNotesSignal()) {
            void this.analyticsService.trackOrderItemNotesEdited();
        }
        const result: {
            notes: string
        } = await this.navigationService.createPage("woa-order-notes-edit-page", {notes: this.orderItem().itemNotesSignal()});
        if (result) {
            this.orderItem().itemNotesSignal.set(result.notes.trim());
        }
    }

    public removeNotes(): void {
        void this.analyticsService.trackOrderItemNotesRemoved();
        this.orderItem().itemNotesSignal.set(undefined);
    }

    public sizeSelected(menuItem: MenuItemData): void {
        this.menuItem.set(menuItem);
        const newOrderItem: OrderItem = this.orderModel.createOrderItem(this.menuItem(), this.orderItem().quantitySignal(), false);
        newOrderItem.assignExtras(this.orderItem(), menuItem);
        newOrderItem.itemNotesSignal.set(this.orderItem().itemNotesSignal());

        // We don't add default extras when creating the order item above, because we want to assign the existing extras from the previously selected size first.
        // Defaults will then only be added for extra groups which were not on the previous size.
        newOrderItem.addDefaultExtras(undefined, undefined, menuItem.sellable.extraGroups);

        this.orderItem.set(newOrderItem);

        while (this.orderItem().quantity > 1 && this.orderItem().isAtLimit()(1, this.orderItem().quantity - 1)) {
            this.orderItem().quantity--;
        }
    }

    public async confirmItem(): Promise<void> {
        if (this.itemConfirmed()) {
            return;
        }

        if (!this.orderItem().quantitySignal()) {
            if (this.editing()) {
                await this.orderModel.removeOrderItem(this.originalOrderItem());
            }
            void this.dismiss(this.orderItem());
            return;
        }

        if (this.menuModel.itemOutOfStock()(this.menuItem())) {
            void this.notificationService.displayAlert(null, "woa.message.order.productOutOfStock", {item: this.menuModel.getMenuItemDisplayName(this.menuItem())});
            return;
        }

        if (!this.orderModel.validateOrderItem(this.orderItem())) {
            return;
        }

        this.itemConfirmed.set(true);

        if (!this.editing()) {
            let revalidate: boolean = false;
            let result: PromotionResult = new PromotionResult(false, true);
            if (this.orderItem().compositeComponentsSignal()) {
                for (const component of this.orderItem().compositeComponentsSignal()) {
                    result = await this.promptForPromotedExtras(<OrderItem>component, this.orderItem());
                    if (result.changesMade) {
                        revalidate = true;
                    }
                    if (!result.proceed) {
                        break;
                    }
                }
            } else {
                result = await this.promptForPromotedExtras(this.orderItem(), this.orderItem());
                if (result.changesMade) {
                    revalidate = true;
                }
            }
            if (revalidate && !this.orderModel.validateOrderItem(this.orderItem())) {
                this.itemConfirmed.set(false);
                return;
            }
            if (!result.proceed) {
                this.itemConfirmed.set(false);
                return;
            }
        }

        if (this.editing()) {
            this.orderModel.replaceOrderItemByIdentifier(this.originalOrderItem().identifier, this.orderItem());
            this.itemsOrdered = true;
            void this.dismiss(this.orderItem());
            return;
        }

        if (this.singleItem()) {
            this.orderModel.addOrderItem(this.orderItem());
            void this.analyticsService.trackItemAddedToOrder(this.orderItem(), this.menuItem() ? this.menuItem().promotedFeatureTag : undefined);
            this.itemsOrdered = true;
            void this.dismiss(this.orderItem());
            return;
        }

        if (this.isLastOrderItem()) {
            this.itemsToOrder().forEach((orderItem: OrderItem) => {
                this.orderModel.addOrderItem(orderItem);
                void this.analyticsService.trackItemAddedToOrder(orderItem);
            });
            this.itemsOrdered = true;
            this.dealModel.applyDeal(this.dealModel.pendingDeal());
            void this.dismiss(this.orderItem());
            return;
        }

        this.orderItemIndex.update(i => i + 1);
        this.contentVisible.set(false);
        setTimeout(() => {
            this.orderItem.set(this.itemsToOrder()[this.orderItemIndex()]);
            this.menuItem.set(this.orderItem().menuItem());
            this.contentVisible.set(true);
            this.itemConfirmed.set(false);
        }, 100);
    }

    /**
     * Prompt for promoted multi-select extras where the user hasn't interacted with the extra group.
     * @param orderItem
     * @param mainOrderItem
     */
    public async promptForPromotedExtras(orderItem: OrderItem, mainOrderItem: OrderItem): Promise<PromotionResult> {
        let anyChangesMade: boolean = false;
        for (const extraGroup of orderItem.availableExtraGroups()) {
            if (!this.menuModel.extraGroupIsPromoted()(extraGroup) || this.hasExtraGroupInteracted(extraGroup) || !orderItem.isExtraGroupConditionallyAvailable(extraGroup)) {
                continue;
            }

            const originalLocalTotal: number = orderItem.localTotalSignal();
            void this.analyticsService.trackExtraPromoShown(mainOrderItem.nameSignal(), orderItem === mainOrderItem ? undefined : orderItem.nameSignal(), extraGroup.name,
                mainOrderItem.sellable().plu, orderItem == mainOrderItem ? undefined : orderItem.sellable().plu);
            this.setExtraGroupInteracted(extraGroup);
            let editOrderItem: OrderItem = orderItem.createClone();
            const changesMode: boolean = await this.navigationService.createPage("woa-order-item-combined-extras-edit-page", {
                orderItem: editOrderItem,
                mainOrderItem: mainOrderItem,
                originalOrderItem: this.originalOrderItem(),
                menuItem: this.menuItem(),
                promotedExtraGroup: extraGroup
            });
            if (changesMode) {
                anyChangesMade = true;
                orderItem.assignExtras(editOrderItem, undefined, true);

                void this.analyticsService.trackExtraPromoOrdered(mainOrderItem.nameSignal(), orderItem === mainOrderItem ? undefined : orderItem.nameSignal(), extraGroup.name,
                    mainOrderItem.sellable().plu, orderItem == mainOrderItem ? undefined : orderItem.sellable().plu, orderItem.localTotalSignal() - originalLocalTotal);
            } else {
                void this.analyticsService.trackExtraPromoCancelled(mainOrderItem.nameSignal(), orderItem === mainOrderItem ? undefined : orderItem.nameSignal(), extraGroup.name,
                    mainOrderItem.sellable().plu, orderItem == mainOrderItem ? undefined : orderItem.sellable().plu);

                // Dismissing the page (as opposed to explicitly declining) stops further promos and returns to editing the product
                if (changesMode !== false) {
                    return new PromotionResult(anyChangesMade, false);
                }
            }
        }
        return new PromotionResult(anyChangesMade, true);
    }
}
